import PropTypes from 'prop-types';
import React from 'react';

import { ContentZonesService, LocaleService } from '@sm360/widget-commons-core-services';

import PurchaseMethodsModel from 'commons-vehicle/PurchaseMethods/models/PurchaseMethodsModel.js';
import { CURRENCY_0, CURRENCY_2 } from 'commons-vehicle/constants/currencyFormats.js';
import { getFormattedCurrencySplit } from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsService/PurchaseMethodsService.js';

import 'commons-vehicle/Vehicle/components/PaymentV2/Financing/Financing.scss';

const Financing = ({ children, purchaseMethods, taxCodes, isUsed, rebates, dealerRebates, electricRebate }) => {
    const term = purchaseMethods.getSelectedFinanceTerm();
    const costOfCredit = purchaseMethods.getSelectedFinanceCostOfCredit();
    const sellingPrice = purchaseMethods.getSelectedFinanceSellingPrice();

    if (!term || term.calculatedPayment <= 0) {
        return (
            <div className="financing-section tile-payment-info">
                <div className="tile-frequency is-bold">
                    {purchaseMethods.getPurchaseMethodLabels(LocaleService).verb}
                </div>
                <span className="tile-payment-info-unavailable">{LocaleService.t('NOT_AVAILABLE')}</span>
            </div>
        );
    }

    const calculatedPaymentSplit = getFormattedCurrencySplit(LocaleService.l(term.calculatedPayment, CURRENCY_2));
    const aprSplit = term.apr.toFixed(2).split('.');

    let includedDisclaimer;
    if (isUsed) {
        includedDisclaimer = ContentZonesService.getContentZone(
            'widget.commons',
            'disclaimer.price.included.used.finance'
        )
            ? ContentZonesService.getContentZone('widget.commons', 'disclaimer.price.included.used.finance').getValue()
            : '';
    }

    const taxe = purchaseMethods.getSelectedFinanceCalculatedPayments().calculatedPaymentTaxes
        ? purchaseMethods.getSelectedFinanceCalculatedPayments().calculatedPaymentTaxes.toFixed(2)
        : 0;

    const totalPrice = purchaseMethods.getSelectedFinanceCalculatedPayments().calculatedPayment
        ? purchaseMethods.getSelectedFinanceCalculatedPayments().calculatedPayment.toFixed(2)
        : 0;

    return (
        <div className="financing-section tile-payment-info">
            <div className="tile-frequency is-bold">
                {purchaseMethods.getPurchaseMethodLabels(LocaleService).verb}
                <span>
                    / {term.term} {LocaleService.t('TERMS.MONTH')}
                </span>
            </div>
            <div className="tile-payment">
                <div className="tile-payment-amount is-bold is-price">
                    {calculatedPaymentSplit.beforeDecimal}
                    <sup>{calculatedPaymentSplit.afterDecimal}</sup>
                    <span>{purchaseMethods.getPaymentFrequencyLabels(false, LocaleService).perPeriod}</span>
                    <div className="tile-term">
                        <span className="is-small is-light">
                            {LocaleService.t('VEHICLE_TILE.TAXES_AND_PRICE', {
                                taxe,
                                taxCodes,
                                totalPrice,
                            })}
                        </span>
                    </div>
                </div>

                <div className="tile-payment-rate">
                    {aprSplit[0]}
                    <sup>{aprSplit[1]}%</sup>
                    <span className="frequency-highlight">
                        / {term.term} {LocaleService.t('TERMS.MONTH')}
                    </span>
                </div>
                <div className="tile-payment-detail small">
                    <div className="tile-payment-detail-wrapper">
                        <span className="tile-payment-detail-note">
                            {LocaleService.t('DOWN_PAYMENTS.TOTAL_CREDIT')}
                        </span>
                        {LocaleService.l(costOfCredit, CURRENCY_2)}
                    </div>
                    <div className="payment-rebates">
                        {rebates}
                        {dealerRebates}
                        {electricRebate}
                    </div>
                </div>

                {includedDisclaimer && <div className="tile-payment-disclaimer-included">{includedDisclaimer}</div>}

                <div className="tile-payment-detail cashdown">
                    <div className="tile-payment-detail-wrapper">
                        <span className="tile-payment-detail-note">
                            {LocaleService.t('DOWN_PAYMENTS.CASH_DOWN_LABEL')}
                        </span>
                        {LocaleService.l(term.cashDown, CURRENCY_0)}
                    </div>
                </div>
                <div className="tile-payment-detail">
                    <div className="tile-payment-detail-wrapper">
                        <span className="tile-payment-detail-note">
                            {LocaleService.t('DOWN_PAYMENTS.SELLING_PRICE')}
                        </span>
                        {LocaleService.l(sellingPrice, CURRENCY_2)}
                    </div>
                </div>
            </div>
            <div className="payment-rebates">{children}</div>
        </div>
    );
};

Financing.defaultProps = {
    children: null,
    isUsed: false,
    rebates: null,
    dealerRebates: null,
    electricRebate: null,
};

Financing.propTypes = {
    children: PropTypes.element,
    purchaseMethods: PropTypes.instanceOf(PurchaseMethodsModel).isRequired,
    taxCodes: PropTypes.string.isRequired,
    isUsed: PropTypes.bool,
    rebates: PropTypes.element,
    dealerRebates: PropTypes.element,
    electricRebate: PropTypes.element,
};

Financing.displayName = 'Financing';

export default Financing;
